/**
 * Route admin `/tutor/admin/animations/$animationId` — « Tester » le brouillon
 * d'une animation créée par l'IA (moteur JRAF, lot 3).
 *
 * Joue le brouillon comme un élève (avec ou sans accès à la partie « À toi »,
 * intro et générique de l'app ou de la vidéo publiée), montre le rapport de
 * validation (erreurs, défauts de mise en page et de rythme), permet de
 * régénérer, d'ENREGISTRER TA VOIX scène par scène (lot 6 : prompteur,
 * Whisper, voix posée dans le brouillon), de MODIFIER le document à la main
 * (JSON vérifié pendant la frappe, en attendant le Studio du lot 5) et de
 * le PUBLIER dans l'app (lot 4 : version immuable revalidée par le serveur,
 * lien public) et en VIDÉO (lot 7 : YouTube Short, TikTok, Reels — voix de
 * synthèse signalée sur les parties sans ta voix, aperçu puis publication).
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, Pencil, SlidersHorizontal } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { isQualityWarning, validateJraf } from '@jeraisonne/animation';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { AnimationAutoFix } from '@/features/animations/animation-auto-fix';
import { AnimationAppPublish } from '@/features/animations/animation-publish-controls';
import { AnimationFitTexts } from '@/features/animations/animation-fit-texts';
import { AnimationVoicePanel } from '@/features/animations/animation-voice-panel';
import { AnimationVersions } from '@/features/animations/animation-versions';
import { AnimationVideoExport } from '@/features/animations/animation-video-export';
import { AnimationJsonEditor } from '@/features/animations/animation-json-editor';
import { AnimationIssues, AnimationTestBench } from '@/features/animations/animation-test-bench';
import { AnimationGenerationControls } from '@/features/cards/card-animation-panel';
import { isGenerating, useAnimation } from '@/lib/animations/api';
import { useCardTools } from '@/lib/cards/card-tools';

export function TutorAdminAnimationTestPage() {
  const { animationId } = useParams({ strict: false }) as { animationId: string };
  const query = useAnimation(animationId);
  const row = query.data;
  // mêmes règles que le serveur : seuls les outils RELIÉS à la carte peuvent s'ouvrir
  const tools = useCardTools(row?.card_id ?? '');
  const toolIds = tools.data;
  const [editing, setEditing] = useState(false);
  const result = useMemo(
    () => (row?.draft && toolIds ? validateJraf(row.draft, { toolIds }) : null),
    [row?.draft, toolIds],
  );

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {row && (
        <Link
          to="/tutor/cards/$cardId"
          params={{ cardId: row.card_id }}
          className="text-primary-600 mb-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Retour à la SKILLCARD
        </Link>
      )}
      <h1 className="font-display text-ink-900 text-[22px] font-extrabold">
        {row?.title || 'Animation'}
      </h1>

      {query.isLoading && <p className="text-ink-500 mt-3 text-sm">Chargement…</p>}
      {query.error && (
        <p role="alert" className="text-error-600 mt-3 text-sm">
          Impossible de charger l’animation.
        </p>
      )}
      {!query.isLoading && !row && !query.error && (
        <p className="text-ink-500 mt-3 text-sm">Animation introuvable.</p>
      )}

      {row && (
        <div className="mt-3 space-y-4">
          <AnimationGenerationControls
            row={row}
            cardId={row.card_id}
            validity={
              result
                ? {
                    valid: result.ok,
                    errors: result.ok ? 0 : result.errors.length,
                    quality: result.warnings.filter(isQualityWarning).length,
                  }
                : undefined
            }
          />
          {!isGenerating(row) && row.draft != null && (
            <Link
              to="/tutor/admin/animations/$animationId/studio"
              params={{ animationId: row.id }}
              className="bg-primary-500 hover:bg-primary-600 inline-flex min-h-11 items-center gap-1.5 rounded-xl px-4 text-sm font-semibold text-white"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden /> Modifier dans le Studio
            </Link>
          )}
          {!isGenerating(row) && result && (
            <>
              {result.ok ? (
                <AnimationTestBench doc={result.doc} />
              ) : (
                <>
                  <AnimationAutoFix row={row} toolIds={toolIds} />
                  <p className="text-ink-600 text-sm">
                    Le brouillon a des erreurs : corrige-les (« Modifier ») ou régénère.
                  </p>
                </>
              )}
              {result.ok && (
                <section aria-label="Ta voix">
                  <h2 className="font-display text-ink-900 mb-2 text-base font-bold">Ta voix</h2>
                  <AnimationVoicePanel
                    row={row}
                    doc={result.doc}
                    toolIds={toolIds}
                    dropped={row.validation?.voices?.dropped}
                  />
                </section>
              )}
              <section aria-label="Publier">
                <h2 className="font-display text-ink-900 mb-2 text-base font-bold">
                  Publier dans l’app
                </h2>
                <AnimationAppPublish
                  row={row}
                  health={{
                    valid: result.ok,
                    quality: result.warnings.filter(isQualityWarning).length,
                  }}
                />
              </section>
              <section aria-label="Vidéo" id="video">
                <h2 className="font-display text-ink-900 mb-2 text-base font-bold">
                  Vidéo · YouTube Short, TikTok, Reels
                </h2>
                <AnimationVideoExport
                  row={row}
                  doc={result.ok ? result.doc : null}
                  levelLabel={
                    result.ok && result.doc.metadata.levels[0]
                      ? levelLabel(result.doc.metadata.levels[0])
                      : null
                  }
                  subjectLabel={result.ok ? subjectLabel(result.doc.metadata.subject) : null}
                />
              </section>
              {row.published_version != null && (
                <section aria-label="Versions publiées">
                  <h2 className="font-display text-ink-900 mb-2 text-base font-bold">
                    Versions publiées
                  </h2>
                  <AnimationVersions row={row} toolIds={toolIds} />
                </section>
              )}
              <section aria-label="Vérification">
                <h2 className="font-display text-ink-900 mb-2 text-base font-bold">Vérification</h2>
                <AnimationFitTexts row={row} toolIds={toolIds} />
                <AnimationIssues
                  errors={result.ok ? [] : result.errors}
                  warnings={result.warnings}
                />
              </section>
              <section aria-label="Modifier">
                <button
                  type="button"
                  aria-expanded={editing}
                  onClick={() => setEditing((e) => !e)}
                  className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold"
                >
                  <Pencil className="h-4 w-4" aria-hidden /> JSON (avancé)
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', editing && 'rotate-180')}
                    aria-hidden
                  />
                </button>
                {editing && (
                  <div className="mt-3">
                    <AnimationJsonEditor row={row} toolIds={toolIds} />
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
}
